export type UiTextSize = "small" | "default" | "large";
export type WindSpeedUnit = "kn" | "ms";

export const DEFAULT_SMHI_BASE_URL =
  "https://opendata-download-metobs.smhi.se/api/version/latest";
export const DEFAULT_OPEN_METEO_URL = "https://api.open-meteo.com/v1/forecast";

export const DEFAULT_AIS_SERVER_URL =
  String(import.meta.env.VITE_AIS_API_BASE_URL ?? "").trim() ||
  "https://ais.klint.zpl.se/";

export interface UiPreferences {
  textSize: UiTextSize;
  darkMode: boolean;
  windSpeedUnit: WindSpeedUnit;
  ownMmsi: string;
  smhiBaseUrl: string;
  openMeteoUrl: string;
  aisServerUrl: string;
}

const STORAGE_KEY = "klint.uiPreferences";
const DEFAULT_PREFERENCES: UiPreferences = {
  textSize: "default",
  darkMode: true,
  windSpeedUnit: "kn",
  ownMmsi: "",
  smhiBaseUrl: DEFAULT_SMHI_BASE_URL,
  openMeteoUrl: DEFAULT_OPEN_METEO_URL,
  aisServerUrl: DEFAULT_AIS_SERVER_URL,
};

function isTextSize(value: unknown): value is UiTextSize {
  return value === "small" || value === "default" || value === "large";
}

function isWindSpeedUnit(value: unknown): value is WindSpeedUnit {
  return value === "kn" || value === "ms";
}

export function normaliseProviderUrl(
  value: string,
  label: string,
  allowEmpty = false,
): string {
  const trimmed = value.trim();
  if (!trimmed) {
    if (allowEmpty) return "";
    throw new Error(`Enter the ${label} URL.`);
  }
  let url: URL;
  try {
    url = new URL(trimmed, location.origin);
  } catch {
    throw new Error(`The ${label} URL is not valid.`);
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error(`The ${label} URL must use HTTP or HTTPS.`);
  }
  url.hash = "";
  const normalised = url.toString().replace(/\/$/, "");
  return trimmed.startsWith("/")
    ? `${url.pathname.replace(/\/$/, "")}${url.search}`
    : normalised;
}

function storedProviderUrl(
  value: unknown,
  fallback: string,
  label: string,
  allowEmpty = false,
): string {
  if (typeof value !== "string") return fallback;
  try {
    return normaliseProviderUrl(value, label, allowEmpty);
  } catch {
    return fallback;
  }
}

export function uiPreferences(): UiPreferences {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "null") as Partial<UiPreferences> | null;
    return {
      textSize: isTextSize(stored?.textSize) ? stored.textSize : DEFAULT_PREFERENCES.textSize,
      darkMode:
        typeof stored?.darkMode === "boolean" ? stored.darkMode : DEFAULT_PREFERENCES.darkMode,
      windSpeedUnit: isWindSpeedUnit(stored?.windSpeedUnit)
        ? stored.windSpeedUnit
        : DEFAULT_PREFERENCES.windSpeedUnit,
      ownMmsi: typeof stored?.ownMmsi === "string" && /^(?:\d{6,9})?$/.test(stored.ownMmsi)
        ? stored.ownMmsi
        : DEFAULT_PREFERENCES.ownMmsi,
      smhiBaseUrl: storedProviderUrl(
        stored?.smhiBaseUrl,
        DEFAULT_PREFERENCES.smhiBaseUrl,
        "SMHI observations",
      ),
      openMeteoUrl: storedProviderUrl(
        stored?.openMeteoUrl,
        DEFAULT_PREFERENCES.openMeteoUrl,
        "Open-Meteo forecast",
      ),
      aisServerUrl: storedProviderUrl(
        stored?.aisServerUrl,
        DEFAULT_PREFERENCES.aisServerUrl,
        "AIS server",
        true,
      ),
    };
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

function apply(preferences: UiPreferences): void {
  document.documentElement.dataset.textSize = preferences.textSize;
  document.documentElement.dataset.theme = preferences.darkMode ? "dark" : "light";
  document
    .querySelector<HTMLMetaElement>('meta[name="theme-color"]')
    ?.setAttribute("content", preferences.darkMode ? "#0b1f2a" : "#f5f9fa");
}

function save(preferences: UiPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // A blocked storage bucket should not make the controls stop working.
  }
  apply(preferences);
}

export function initializeUiPreferences(): void {
  apply(uiPreferences());
}

export function setUiTextSize(textSize: UiTextSize): void {
  save({ ...uiPreferences(), textSize });
}

export function setDarkMode(darkMode: boolean): void {
  save({ ...uiPreferences(), darkMode });
}

export function setWindSpeedUnit(windSpeedUnit: WindSpeedUnit): void {
  save({ ...uiPreferences(), windSpeedUnit });
}

export function normaliseOwnMmsi(value: string): string {
  const trimmed = value.trim();
  if (trimmed && !/^\d{6,9}$/.test(trimmed)) {
    throw new Error("Own vessel MMSI must contain 6 to 9 digits.");
  }
  return trimmed;
}

export function setOwnMmsi(ownMmsi: string): void {
  save({ ...uiPreferences(), ownMmsi: normaliseOwnMmsi(ownMmsi) });
}

export interface ProviderUrls {
  smhiBaseUrl: string;
  openMeteoUrl: string;
  aisServerUrl: string;
}

export function setProviderUrls(urls: ProviderUrls): ProviderUrls {
  const normalised = {
    smhiBaseUrl: normaliseProviderUrl(urls.smhiBaseUrl, "SMHI observations"),
    openMeteoUrl: normaliseProviderUrl(urls.openMeteoUrl, "Open-Meteo forecast"),
    aisServerUrl: normaliseProviderUrl(urls.aisServerUrl, "AIS server", true),
  };
  save({ ...uiPreferences(), ...normalised });
  return normalised;
}
